import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, GridRow, GridItem, Icon, Spacer, useSnackbar, TabPanel, Tabs } from '@emburse/embark-core';
import { LaunchIcon } from '@emburse/embark-icons';
import { DataGridHeader } from '../../components';
import SingleColumnLayout from '../../components/Layouts/SingleColumnLayout';
import DataInterchangeTabView from './DataInterchangeTabView/DataInterchangeTabView';
import InterchangeTable from './InterchangeTable/InterchangeTable';
import InterchangeTableProvider from './InterchangeTable/InterchangeTableProvider';
import FileTable from './FileTable/FileTable';
import HistoryTable from './HistoryTable/HistoryTable';
import { getDataInterchanges, getDataInterchangeFiles, getDataInterchangeHistory } from '../../api/dataInterchange';
import { isValidDate } from '../../util/dateAdapter';
import { $t, useTranslation } from '../../hooks/useTranslation';
const isActiveInUI = (row) => {
    const requiredDays = [
        'Sched_Sun',
        'Sched_Mon',
        'Sched_Tue',
        'Sched_Wed',
        'Sched_Thu',
        'Sched_Fri',
        'Sched_Sat'
    ];
    const hasProperty = Object.keys(row).some((item) => requiredDays.includes(item));
    const isTrue = requiredDays.some((item) => !!row[item]);
    return (isValidDate(new Date(row.Sched_StartTime)) &&
        isValidDate(new Date(row.Sched_EndTime)) &&
        hasProperty &&
        isTrue);
};
const transformInterchangeData = (row) => ({
    ...row,
    id: window.location.search.includes(String(row.PK)) ? '0' : row.PK,
    LastStart: row.LastStart ? `${row.LastStart}` : '',
    isActiveInUI: isActiveInUI(row)
});
const transformHistoryData = (row, i) => ({
    ...row,
    id: i
});
const transformFileData = (folder, i) => ({
    ...folder,
    id: i, // offset from our two pre-existing folders
    Name: folder.Path,
    // Assign a unique ID to each file in each folder
    Files: folder.Files.map((file, j) => ({
        ...file,
        id: j + 5,
        Name: file.Filename,
        Type: 'file'
    }))
});
function CorpDataInterchange() {
    useTranslation();
    const didMount = useRef(false);
    const [tab, setTab] = useState(0);
    const [activityTabLoading, setActivityTabLoading] = useState(true);
    const [activityData, setActivityData] = useState([]);
    const [historyTabLoading, setHistoryTabLoading] = useState(true);
    const [historyData, setHistoryData] = useState([]);
    const [fileTabLoading, setFileTabLoading] = useState(true);
    const [fileData, setFileData] = useState([]);
    const { enqueueSnackbar } = useSnackbar();
    const updateDataInterchanges = useCallback(() => {
        setActivityTabLoading(true);
        getDataInterchanges().then((resp) => {
            const data = JSON.parse(resp.Data);
            setActivityData([...data.map(transformInterchangeData)]);
            setActivityTabLoading(false);
        });
    }, []);
    const updateFileData = () => {
        setFileTabLoading(true);
        getDataInterchangeFiles(activityData).then((resp) => {
            setFileData([...resp.map(transformFileData)]);
            setFileTabLoading(false);
        });
    };
    const updateHistoryData = () => {
        setHistoryTabLoading(true);
        getDataInterchangeHistory().then((resp) => {
            const data = JSON.parse(resp.Data);
            setHistoryData([...data.map(transformHistoryData)]);
            setHistoryTabLoading(false);
        });
    };
    const pageInitLoad = () => {
        if (!didMount.current) {
            updateDataInterchanges();
            if (window.location.search.includes('__ID=')) {
                enqueueSnackbar(window.location.search.includes('new')
                    ? $t('A new Data Interchange has been created')
                    : $t('The Data Interchange has been changed'), { variant: 'alert', AlertProps: { severity: 'success' } });
            }
            didMount.current = true;
        }
    };
    useEffect(pageInitLoad, [updateDataInterchanges, enqueueSnackbar]);
    const handleChange = (_event, newTabIndex) => {
        setTab(newTabIndex);
        // If we're switching to the history tab and it hasn't been loaded yet
        if (newTabIndex === 1 && historyTabLoading === true) {
            updateHistoryData();
            // if we're switching to the file tab and it hasn't been loaded yet
        }
        else if (newTabIndex === 2 && fileTabLoading === true) {
            updateFileData();
        }
    };
    const tabs = [{ label: $t('Interchanges') }, { label: $t('History') }, { label: $t('Files') }];
    return (_jsxs(SingleColumnLayout, { children: [_jsxs(GridRow, { justifyContent: "space-between", children: [_jsx(GridItem, { children: _jsx(DataGridHeader, { title: $t('Data Interchanges') }) }), _jsx(GridItem, { children: _jsx(Button, { onClick: () => window.open('./CorpDataInterchangeConfig.aspx', '_self'), children: $t('Create New Interchange') }) })] }), _jsx(Button, { variant: "text", color: "primary", startIcon: _jsx(Icon, { iconUrl: LaunchIcon }), onClick: () => window.open('https://help.certify.com/hc/en-us/articles/9116013966491', '_blank'), children: $t('Help Center Documentation') }), _jsx(Spacer, { size: 16 }), _jsx(Tabs, { tabs: tabs, id: "data-interchange-tabs", onChange: handleChange }), _jsx(TabPanel, { value: tab, index: 0, children: _jsx(DataInterchangeTabView, { children: _jsx(InterchangeTableProvider, { children: _jsx(InterchangeTable, { loading: activityTabLoading, data: activityData, updateData: updateDataInterchanges }) }) }) }), _jsx(TabPanel, { value: tab, index: 1, children: _jsx(DataInterchangeTabView, { children: _jsx(HistoryTable, { loading: historyTabLoading, data: historyData, updateData: updateHistoryData }) }) }), _jsx(TabPanel, { value: tab, index: 2, children: _jsx(DataInterchangeTabView, { children: _jsx(FileTable, { loading: fileTabLoading, data: fileData, updateData: updateFileData }) }) })] }));
}
export default CorpDataInterchange;
